import { useState } from 'react';
import { usersApi, UserDetail } from '../api/users';
import { playgroundUsersApi, PlaygroundUser } from '../api/playgroundUsers';
import { adminUsersApi } from '../api/adminUsers';
import type { AdminUser as AdminUserType } from '../api/adminUsers';

interface UsePlaygroundUsersParams {
  adminUserId: string | undefined;
  onError: (msg: string) => void;
}

export function usePlaygroundUsers({ adminUserId, onError }: UsePlaygroundUsersParams) {
  // User list state
  const [users, setUsers] = useState<PlaygroundUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);

  // Sharing dialog state
  const [sharingDialogUserId, setSharingDialogUserId] = useState<string | null>(null);
  const [sharingDialogSettings, setSharingDialogSettings] = useState<PlaygroundUser['settings'] | null>(null);
  const [adminUsersList, setAdminUsersList] = useState<AdminUserType[]>([]);
  const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>([]);
  const [shareToAll, setShareToAll] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);

  const loadUsers = async () => {
    if (!adminUserId) return;
    try {
      setUsersLoading(true);
      const response = await playgroundUsersApi.list(adminUserId);
      setUsers(response.users);
    } catch (err) {
      console.error('Failed to load users:', err);
      onError('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadUserDetails = async (userId: string) => {
    try {
      const user = await usersApi.getById(userId);
      setSelectedUser(user);
    } catch (err) {
      console.error('Failed to load user details:', err);
    }
  };

  const handleCreateUser = async (callbacks: {
    onCreated: (userId: string) => void;
    clearMessages: () => void;
    clearMemories: () => void;
    resetTab: () => void;
  }) => {
    try {
      onError('');
      const agentUser = await usersApi.create({
        user: { name: '새 사용자', language: 'ko', birth_date: '1990-01-01' },
        device: { platform: 'web', timezone: 'Asia/Seoul' },
        settings: {
          source: 'playground',
          admin_user_ids: [adminUserId!],
          is_pinned: false,
        },
      });
      await loadUsers();
      callbacks.onCreated(agentUser.user_id);
      callbacks.clearMessages();
      callbacks.clearMemories();
      callbacks.resetTab();
    } catch (err) {
      console.error('Failed to create user:', err);
      onError('사용자 생성에 실패했습니다.');
    }
  };

  const handleDeleteUser = async (
    userId: string,
    e: React.MouseEvent,
    callbacks: {
      selectedUserId: string | null;
      onDeselect: () => void;
      clearMessages: () => void;
      clearMemories: () => void;
      clearThreads: () => void;
    },
  ) => {
    e.stopPropagation();
    if (!confirm('이 사용자를 삭제하시겠습니까?')) return;

    try {
      await usersApi.delete(userId);
    } catch (err) {
      console.warn('User may have been already deleted:', err);
    }

    if (callbacks.selectedUserId === userId) {
      callbacks.onDeselect();
      setSelectedUser(null);
      callbacks.clearMessages();
      callbacks.clearMemories();
      callbacks.clearThreads();
    }
    await loadUsers();
  };

  const handleCloneUser = async (
    userId: string,
    e: React.MouseEvent,
    callbacks: {
      onCloned: (newUserId: string) => void;
      clearMessages: () => void;
      clearMemories: () => void;
      clearThreads: () => void;
    },
  ) => {
    e.stopPropagation();
    if (!adminUserId) return;
    if (!confirm('이 사용자를 복제하시겠습니까?\n대화·메모리·문서·체크포인트 전체를 복제하며, 데이터가 많으면 수십 초 걸릴 수 있습니다.')) return;
    try {
      onError('');
      // Clone lands in this admin's playground (source=playground + admin_user_ids).
      const result = await usersApi.clone(userId, adminUserId);
      await loadUsers();
      callbacks.onCloned(result.user_id);
      callbacks.clearMessages();
      callbacks.clearMemories();
      callbacks.clearThreads();
    } catch (err) {
      console.error('Failed to clone user:', err);
      onError('사용자 복제에 실패했습니다.');
    }
  };

  const handleTogglePinUser = async (userId: string, settings: PlaygroundUser['settings'], e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await playgroundUsersApi.togglePin(userId, settings);
      await loadUsers();
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      onError('고정 상태 변경에 실패했습니다.');
    }
  };

  const openSharingDialog = async (userId: string, settings: PlaygroundUser['settings'], e: React.MouseEvent) => {
    e.stopPropagation();
    setSharingDialogUserId(userId);
    setSharingDialogSettings(settings);
    const currentIds = settings?.admin_user_ids || [];
    setShareToAll(currentIds.includes('all'));
    setSelectedAdminIds(currentIds.filter((id: string) => id !== 'all'));

    try {
      const response = await adminUsersApi.list();
      setAdminUsersList(response.users);
    } catch (err) {
      console.error('Failed to load admin users:', err);
    }
  };

  const handleSaveSharing = async () => {
    if (!sharingDialogUserId || !sharingDialogSettings) return;
    setSharingLoading(true);
    try {
      const adminIds = shareToAll ? ['all'] : selectedAdminIds;
      await playgroundUsersApi.updateSharing(sharingDialogUserId, sharingDialogSettings, adminIds);
      setSharingDialogUserId(null);
      await loadUsers();
    } catch (err) {
      console.error('Failed to save sharing:', err);
      onError('공유 설정 저장에 실패했습니다.');
    } finally {
      setSharingLoading(false);
    }
  };

  return {
    users,
    usersLoading,
    selectedUser,
    setSelectedUser,
    loadUsers,
    loadUserDetails,
    handleCreateUser,
    handleDeleteUser,
    handleCloneUser,
    handleTogglePinUser,
    // Sharing dialog
    sharingDialogUserId,
    setSharingDialogUserId,
    sharingDialogSettings,
    adminUsersList,
    selectedAdminIds,
    setSelectedAdminIds,
    shareToAll,
    setShareToAll,
    sharingLoading,
    openSharingDialog,
    handleSaveSharing,
  };
}
